import { useState } from "react";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { importLeads } from "app/redux/slice/leadSlice";
import { downloadLeadImportSample } from "app/helpers/csvExport";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED = /\.(xlsx|xls|csv)$/i;
const MAX_ERRORS_SHOWN = 10;

const SummaryTile = ({ label, value, color }) => (
  <Grid item xs={6} sm={3}>
    <Box
      sx={{
        p: 1.5,
        textAlign: "center",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      <Typography variant="h3" mb={0} color={color}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={0}>
        {label}
      </Typography>
    </Box>
  </Grid>
);

// The parent refreshes the lead list in its own onClose handler, not here: the
// lead page swaps the whole page for a <Loader /> while the list reloads, which
// unmounts this dialog and would wipe the summary before it could be read.
const ImportLeads = ({ onClose }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSelect = (event) => {
    const picked = event.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
    if (!picked) return;

    if (!ALLOWED.test(picked.name)) {
      setFileError("Please choose an Excel (.xlsx, .xls) or CSV file.");
      setFile(null);
      return;
    }
    if (picked.size > MAX_FILE_SIZE) {
      setFileError("File size should be less than 5MB.");
      setFile(null);
      return;
    }

    setFileError("");
    setFile(picked);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);

    const formData = new FormData();
    formData.append("file", file);

    const response = await dispatch(importLeads(formData)).unwrap();
    const isSuccess = response.status === 200;

    enqueueSnackbar(response.data?.responseMessage || "Import failed", {
      variant: isSuccess ? "success" : "error",
    });

    if (isSuccess) {
      setResult(response.data?.data?.leadImport ?? null);
    }
    setImporting(false);
  };


  // ---- Summary view, shown once an import has run ----
  if (result) {
    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <SummaryTile label="Total rows" value={result.totalRows ?? 0} />
          <SummaryTile
            label="Imported"
            value={result.created ?? 0}
            color="success.main"
          />
          <SummaryTile
            label="Skipped"
            value={result.skipped ?? 0}
            color="warning.main"
          />
          <SummaryTile
            label="Failed"
            value={result.failed ?? 0}
            color="error.main"
          />
        </Grid>

        {result.errors?.length > 0 && (
          <>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="h5" mb={1}>
              Rows not imported
            </Typography>
            <Box
              component="ul"
              sx={{ pl: 3, m: 0, maxHeight: 200, overflowY: "auto" }}
            >
              {result.errors.slice(0, MAX_ERRORS_SHOWN).map((message) => (
                <li key={message}>
                  <Typography variant="body2" mb={0}>
                    {message}
                  </Typography>
                </li>
              ))}
            </Box>
            {result.errors.length > MAX_ERRORS_SHOWN && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Showing first {MAX_ERRORS_SHOWN} of {result.errors.length}.
              </Typography>
            )}
          </>
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <LoadingButton variant="contained" size="large" onClick={onClose}>
            Done
          </LoadingButton>
        </Stack>
      </Box>
    );
  }

  // ---- Upload view ----
  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 1 }}>
        Upload an Excel or CSV file with these columns:
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <strong>name</strong>, <strong>number</strong>, <strong>city</strong> —
        only <strong>number</strong> is required. Allowed types: XLSX, XLS, CSV.
        Max size 5MB, up to 2000 rows.
      </Typography>

      <Link
        component="button"
        type="button"
        underline="always"
        onClick={downloadLeadImportSample}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 2 }}
      >
        <CloudDownloadIcon fontSize="small" />
        Download sample file
      </Link>

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mt: 1, mb: 1 }}
      >
        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUploadIcon />}
        >
          Choose File
          <input
            hidden
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleSelect}
          />
        </Button>
        <Typography variant="body2" color="text.secondary" mb={0}>
          {file ? file.name : "No file selected"}
        </Typography>
      </Stack>

      {fileError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fileError}
        </Alert>
      )}

      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        sx={{ mt: 3 }}
      >
        <LoadingButton
          variant="outlined"
          color="secondary"
          size="large"
          onClick={onClose}
          disabled={importing}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          variant="contained"
          size="large"
          onClick={handleImport}
          disabled={!file}
          loading={importing}
          loadingIndicator={<CircularProgress size={20} />}
        >
          Import
        </LoadingButton>
      </Stack>
    </Box>
  );
};

export default ImportLeads;
